import React, { useState, useCallback, useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import config from "./config";
import "./Settings.css";

const PREFERENCE_CATEGORIES = [
  "Communication Style",
  "Format Preferences",
  "Explicit Instructions",
  "Topic Preferences",
  "Tone & Style",
  "General",
];

const Settings = () => {
  const { user } = useAuth();
  const [htmlFile, setHtmlFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [themePreference, setThemePreference] = useState('system'); // 'light', 'dark', or 'system'

  // Chat Memory state
  const [prefItems, setPrefItems] = useState([]);
  const [prefLoading, setPrefLoading] = useState(false);
  const [prefError, setPrefError] = useState("");
  const [prefEditingId, setPrefEditingId] = useState(null);
  const [prefEditCategory, setPrefEditCategory] = useState("");
  const [prefEditContent, setPrefEditContent] = useState("");
  const [prefNewCategory, setPrefNewCategory] = useState("General");
  const [prefNewContent, setPrefNewContent] = useState("");
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefShowAdd, setPrefShowAdd] = useState(false);

  // Cookie helper functions
  const setCookie = (name, value) => {
    // Set cookie with 10 year expiration (essentially never expires)
    const maxAge = 10 * 365 * 24 * 60 * 60; // 10 years in seconds
    document.cookie = `${name}=${value}; max-age=${maxAge}; path=/; SameSite=Lax`;
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const deleteCookie = (name) => {
    document.cookie = `${name}=; max-age=0; path=/; SameSite=Lax`;
  };

  // Apply theme to document
  const applyTheme = (isDark) => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    setIsDarkMode(isDark);
  };

  // Get system preference
  const getSystemPreference = () => {
    if (window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  };

  // Initialize theme on mount and sync with document
  useEffect(() => {
    const manualTheme = getCookie('theme-manual');

    if (manualTheme) {
      // User has manually set a preference, use it
      applyTheme(manualTheme === 'dark');
    } else {
      // No manual preference, use system preference
      const systemPrefersDark = getSystemPreference();
      applyTheme(systemPrefersDark);

      // Listen for system preference changes (only when no manual override)
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        // Only apply if user hasn't manually set a preference
        if (!getCookie('theme-manual')) {
          applyTheme(e.matches);
        }
      };

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    }
  }, []);

  // Sync toggle state with document's theme attribute
  useEffect(() => {
    const syncTheme = () => {
      const currentTheme = document.documentElement.getAttribute('data-theme');
      setIsDarkMode(currentTheme === 'dark');
    };

    // Initial sync
    syncTheme();

    // Create observer to watch for attribute changes
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // Handle theme preference change
  const handleThemeChange = (preference) => {
    setThemePreference(preference);

    if (preference === 'system') {
      // Remove manual override, follow system preferences
      deleteCookie('theme-manual');
      const systemPrefersDark = getSystemPreference();
      applyTheme(systemPrefersDark);
    } else {
      // Set manual preference
      setCookie('theme-manual', preference);
      applyTheme(preference === 'dark');
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext === "html") {
        setHtmlFile(file);
        setFileName(file.name);
      } else {
        alert("Only HTML files are allowed.");
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      "text/html": [".html"],
    },
  });

  const handleSubmit = async () => {
    if (!htmlFile) {
      alert("Please select an HTML file first.");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("html_file", htmlFile);

      const response = await axios.post(
        `${config.api.baseUrl}/chatbot/import_chatgpt_conversations`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      if (response.data.success) {
        alert(
          `Success! ${response.data.imported_count} conversation(s) imported to your chat history.`
        );
        // Reset after successful upload
        setHtmlFile(null);
        setFileName("");
      } else {
        alert("Upload completed but no conversations were imported.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      const errorMsg = error.response?.data?.error || "Failed to upload file. Please try again.";
      alert(`Error: ${errorMsg}`);
    } finally {
      setIsUploading(false);
    }
  };

  // ─── Chat Memory (User Preferences) ─────────────────────────────────────────

  const loadPreferences = useCallback(async () => {
    try {
      setPrefLoading(true);
      setPrefError("");
      const response = await fetch(`${config.api.baseUrl}/chatbot/user-preferences`, {
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to load preferences");
      }
      setPrefItems(data.items || []);
    } catch (err) {
      setPrefError(err.message || "Failed to load chat memory");
    } finally {
      setPrefLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const handleAddPreference = async () => {
    const content = prefNewContent.trim();
    if (!content) return;
    try {
      setPrefSaving(true);
      setPrefError("");
      const response = await fetch(`${config.api.baseUrl}/chatbot/user-preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ category: prefNewCategory, content }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to add preference");
      }
      setPrefItems((prev) => [...prev, data.item]);
      setPrefNewContent("");
      setPrefShowAdd(false);
    } catch (err) {
      setPrefError(err.message);
    } finally {
      setPrefSaving(false);
    }
  };

  const handleStartEdit = (item) => {
    setPrefEditingId(item.id);
    setPrefEditCategory(item.category || "General");
    setPrefEditContent(item.content || "");
  };

  const handleCancelEdit = () => {
    setPrefEditingId(null);
    setPrefEditCategory("");
    setPrefEditContent("");
  };

  const handleSaveEdit = async (itemId) => {
    const content = prefEditContent.trim();
    if (!content) return;
    try {
      setPrefSaving(true);
      setPrefError("");
      const response = await fetch(`${config.api.baseUrl}/chatbot/user-preferences/${itemId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ category: prefEditCategory, content }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to update preference");
      }
      setPrefItems((prev) =>
        prev.map((it) =>
          it.id === itemId ? { ...it, category: prefEditCategory, content } : it
        )
      );
      handleCancelEdit();
    } catch (err) {
      setPrefError(err.message);
    } finally {
      setPrefSaving(false);
    }
  };

  const handleDeletePreference = async (itemId) => {
    try {
      setPrefError("");
      const response = await fetch(`${config.api.baseUrl}/chatbot/user-preferences/${itemId}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to delete preference");
      }
      setPrefItems((prev) => prev.filter((it) => it.id !== itemId));
    } catch (err) {
      setPrefError(err.message);
    }
  };

  // Group items by category for display
  const groupedItems = prefItems.reduce((acc, item) => {
    const cat = item.category || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="settings-page-wrapper">
        <div className="settings-container">
          <h1>Settings</h1>
          <div className="settings-content">
          <div className="settings-section">
            <h2>User Profile</h2>
            <div className="user-profile">
              <p>
                <strong>Name:</strong> {user?.first_name} {user?.last_name}
              </p>
              <p>
                <strong>Email:</strong> {user?.email}
              </p>
            </div>
          </div>

          <div className="settings-section">
            <h2>Appearance</h2>
            <div className="appearance-section">
              <div className="theme-option">
                <div className="theme-info">
                  <div className="theme-label">Theme</div>
                  <div className="theme-description">
                    Choose your preferred theme or follow system preferences
                  </div>
                </div>
                <div className="theme-selector">
                  <label className={`theme-choice ${themePreference === 'light' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="theme"
                      value="light"
                      checked={themePreference === 'light'}
                      onChange={() => handleThemeChange('light')}
                    />
                    <span className="theme-choice-label">Light</span>
                  </label>
                  <label className={`theme-choice ${themePreference === 'dark' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="theme"
                      value="dark"
                      checked={themePreference === 'dark'}
                      onChange={() => handleThemeChange('dark')}
                    />
                    <span className="theme-choice-label">Dark</span>
                  </label>
                  <label className={`theme-choice ${themePreference === 'system' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="theme"
                      value="system"
                      checked={themePreference === 'system'}
                      onChange={() => handleThemeChange('system')}
                    />
                    <span className="theme-choice-label">System</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Chat Memory Section - spans full width */}
          <div className="settings-section settings-section-full">
            <div className="pref-header">
              <div>
                <h2>Chat Memory</h2>
                <p className="pref-description">
                  Things the AI remembers about you across conversations. These are built automatically from your chats, but you can also add, edit, or remove them.
                </p>
              </div>
              <div className="pref-header-actions">
                <button
                  className="pref-add-btn"
                  onClick={() => setPrefShowAdd(!prefShowAdd)}
                >
                  {prefShowAdd ? "Cancel" : "Add Item"}
                </button>
              </div>
            </div>
            <div className="pref-section">
              {prefError && <div className="pref-error">{prefError}</div>}

              {/* Add new item form */}
              {prefShowAdd && (
                <div className="pref-add-form">
                  <select
                    className="pref-category-select"
                    value={prefNewCategory}
                    onChange={(e) => setPrefNewCategory(e.target.value)}
                  >
                    {PREFERENCE_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="pref-content-input"
                    placeholder="e.g., Always use bullet points in responses"
                    value={prefNewContent}
                    onChange={(e) => setPrefNewContent(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddPreference()}
                  />
                  <button
                    className="pref-save-btn"
                    onClick={handleAddPreference}
                    disabled={prefSaving || !prefNewContent.trim()}
                  >
                    {prefSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              )}

              {prefLoading && <div className="pref-loading">Loading chat memory...</div>}

              {!prefLoading && prefItems.length === 0 && (
                <div className="pref-empty">
                  No memories yet. The AI will learn your preferences as you chat, or you can add them manually.
                </div>
              )}

              {!prefLoading && Object.keys(groupedItems).length > 0 && (
                <div className="pref-groups">
                  {Object.entries(groupedItems).map(([category, items]) => (
                    <div className="pref-group" key={category}>
                      <div className="pref-group-label">{category}</div>
                      <div className="pref-group-items">
                        {items.map((item) => (
                          <div className="pref-item" key={item.id}>
                            {prefEditingId === item.id ? (
                              <div className="pref-item-edit">
                                <select
                                  className="pref-category-select"
                                  value={prefEditCategory}
                                  onChange={(e) => setPrefEditCategory(e.target.value)}
                                >
                                  {PREFERENCE_CATEGORIES.map((cat) => (
                                    <option key={cat} value={cat}>{cat}</option>
                                  ))}
                                </select>
                                <input
                                  type="text"
                                  className="pref-content-input"
                                  value={prefEditContent}
                                  onChange={(e) => setPrefEditContent(e.target.value)}
                                  onKeyDown={(e) => e.key === "Enter" && handleSaveEdit(item.id)}
                                />
                                <div className="pref-item-edit-actions">
                                  <button
                                    className="pref-save-btn"
                                    onClick={() => handleSaveEdit(item.id)}
                                    disabled={prefSaving || !prefEditContent.trim()}
                                  >
                                    Save
                                  </button>
                                  <button className="pref-cancel-btn" onClick={handleCancelEdit}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="pref-item-content">{item.content}</div>
                                <div className="pref-item-actions">
                                  <button
                                    className="pref-action-btn pref-edit-btn"
                                    onClick={() => handleStartEdit(item)}
                                    title="Edit"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  </button>
                                  <button
                                    className="pref-action-btn pref-delete-btn"
                                    onClick={() => handleDeletePreference(item.id)}
                                    title="Delete"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="settings-section">
            <h2>ChatGPT Conversation Upload</h2>
            <div className="chatgpt-upload-section">
              <div
                {...getRootProps()}
                className={`html-file-drop ${isDragActive ? "drag-active" : ""} ${htmlFile ? "file-uploaded" : ""}`}
              >
                <input {...getInputProps()} />
                {fileName ? (
                  <p>File Selected: {fileName}</p>
                ) : isDragActive ? (
                  <p>Drop the HTML file here...</p>
                ) : (
                  <p>Drag & drop an HTML file here, or click to select one</p>
                )}
              </div>
              <button
                className={`submit-html-btn ${isUploading || !htmlFile ? "disabled" : ""}`}
                onClick={handleSubmit}
                disabled={isUploading || !htmlFile}
              >
                {isUploading ? "Uploading..." : "Submit Conversation"}
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
